import { useCallback, useEffect, useRef, useState } from "react";
import { reportSettingsApi, type ReportFilterPreset } from "../services/report-settings.api";

export interface UseReportPresetsResult<F> {
  presets: ReportFilterPreset[];
  isLoading: boolean;
  /** Varsayılan preset yüklendiğinde bir kez tetiklenen filtre. */
  defaultFilters: F | null;
  save: (name: string, filters: F, isDefault?: boolean) => Promise<void>;
  remove: (presetID: string) => Promise<void>;
  refresh: () => void;
}

/**
 * Kayıtlı filtre setleri (R11).
 *
 * Rapor filtreleri her ziyarette sıfırlanıyordu; sık kullanılan bir kombinasyonu
 * (ör. "Bu çeyrek – Elektrik Birimi") her seferinde elle kurmak gerekiyordu.
 * Bu hook setleri sunucuda kullanıcı bazında saklar ve varsayılan işaretlenen
 * seti sayfa açılışında bir kez uygular.
 */
// `F` üzerinde Record<string, unknown> kısıtı yok: rapor filtreleri index imzası
// olmayan arayüzler (ör. ReportFilterState) ile tanımlanıyor.
export function useReportPresets<F extends object>(reportKey: string): UseReportPresetsResult<F> {
  const [presets, setPresets] = useState<ReportFilterPreset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [defaultFilters, setDefaultFilters] = useState<F | null>(null);
  // Varsayılan yalnızca ilk yüklemede uygulanır; sonraki refresh'lerde kullanıcının
  // o an ekranda kurduğu filtreyi ezmemeli.
  const defaultAppliedRef = useRef(false);

  const load = useCallback(() => {
    setIsLoading(true);
    reportSettingsApi
      .listPresets(reportKey)
      .then((items) => {
        setPresets(items);
        if (!defaultAppliedRef.current) {
          defaultAppliedRef.current = true;
          const fallback = items.find((p) => p.isDefault);
          if (fallback) setDefaultFilters(fallback.filters as F);
        }
      })
      .catch(() => setPresets([]))
      .finally(() => setIsLoading(false));
  }, [reportKey]);

  useEffect(() => {
    defaultAppliedRef.current = false;
    load();
  }, [load]);

  const save = useCallback(
    async (name: string, filters: F, isDefault = false) => {
      await reportSettingsApi.savePreset(reportKey, {
        name,
        filters: filters as unknown as Record<string, unknown>,
        isDefault,
      });
      load();
    },
    [reportKey, load],
  );

  const remove = useCallback(
    async (presetID: string) => {
      await reportSettingsApi.deletePreset(presetID);
      load();
    },
    [load],
  );

  return { presets, isLoading, defaultFilters, save, remove, refresh: load };
}
